import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { CmsApplianceDetailsPage, SelfFixRoutes } from '@self-fix/util-models';
import { ArticleSearchService } from '@self-fix/data-access';
import { map, take, filter, zip, Observable } from 'rxjs';
import { AppEnvironment } from 'apps/dg-self-fix/src/app/models/environment/app-environment.type';
import { ENVIRONMENT } from '@domgen/dgx-fe-config';
import {
  MyAccountService,
  PlanClaimsStore,
  assistanceType,
  LoadingStatus,
} from '@self-fix/data-access';
export interface ViewModel {
  cmsData: CmsApplianceDetailsPage;
}

@Injectable()
export class ApplianceDetailsComponentService {
  constructor(
    private readonly cmsContainer: CmsContainer<CmsApplianceDetailsPage>,
    private planClaimsStore: PlanClaimsStore,
    readonly myAccountService: MyAccountService,
    readonly articleSearchService: ArticleSearchService,
    private readonly router: Router,
    @Inject(ENVIRONMENT) private readonly environment: AppEnvironment
  ) {
    if (this.environment.brandConfig.variant) {
      this.popularVideosBlock$ = zip(
        this.cmsContainer.content$,
        this.articleSearchService.getRandomVideos()
      ).pipe(
        map((data: [CmsApplianceDetailsPage, any[]]) => {
          const [pageData, randomVideos] = data;
          const blockDTO = pageData.popularVideosBlock;
          return {
            ...blockDTO,
            popularVideosBody:
              'Here is a selection of our most popular how-to videos for ' +
              (this.environment.brandConfig.variant === 'heating'
                ? 'heating'
                : 'home appliances') +
              '.',
            popularVideos: randomVideos,
          };
        })
      );
    } else {
      this.popularVideosBlock$ = this.cmsContainer.content$.pipe(
        map((pageData: CmsApplianceDetailsPage) => {
          const blockDTO = pageData.popularVideosBlock;
          return {
            ...blockDTO,
            popularVideos:
              blockDTO?.popularVideos?.map((video: any) => ({
                title: video.popularVideo.articleTitle,
                body: video.popularVideo.introText || '',
                videoId: video.popularVideo.videoId,
                navigationLink: {
                  href:
                    video.popularVideo['@path']?.replace(
                      /\/([A-Za-z-]+\/){3}/,
                      ''
                    ) || '',
                  label:
                    video.popularVideo.articleTitle ||
                    video.popularVideo['@name'],
                },
              })) || [],
          };
        })
      );
    }
  }

  navigate(applianceCode: string): void {
    this.router.navigate([SelfFixRoutes.YourAppliance], {
      queryParams: { applianceCode },
    });
    return;
  }

  setActivePlan(planNumber: string) {
    this.planClaimsStore.setActivePlan(planNumber);
    localStorage.setItem('planNumber', planNumber);
    this.planClaimsStore.setStatus(LoadingStatus.Loading);
    this.myAccountService
      .getPlanClaims(planNumber)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.planClaimsStore.setPlanClaims(res.result);
        this.planClaimsStore.setStatus(LoadingStatus.Loaded);
      });
  }

  setAssistance(assistance: assistanceType) {
    if (assistance && assistance !== 'None') {
      localStorage.setItem('assistance', assistance);
      this.planClaimsStore.setAssistance(assistance);
    }
  }

  public readonly applianceCategories$ = this.cmsContainer.content$.pipe(
    map((pageData: CmsApplianceDetailsPage) => {
      let categories = this.filterCategoriesByAppEnv(pageData);

      return categories.map((list: any) => ({
        label: list.fieldLabel,
        value: list.fieldValue,
        icon: list.icon.name,
      }));
    })
  );

  public readonly applianceCategoriesTitle$ = this.cmsContainer.content$.pipe(
    map(
      (pageData: CmsApplianceDetailsPage) =>
        pageData.applianceCategoryField.fieldLabel
    )
  );

  public popularVideosBlock$: Observable<any>;

  private filterCategoriesByAppEnv(pageData: CmsApplianceDetailsPage) {
    const filtering = this.environment.brandConfig.variant;
    let mList = pageData.applianceCategoryField.multiFieldList;
    if (filtering === 'heating') {
      mList = mList.filter(
        (list: any) => list.fieldLabel.toLowerCase() === 'heating'
      );
    } else if (filtering === 'white') {
      mList = mList.filter(
        (list: any) => list.fieldLabel.toLowerCase() !== 'heating'
      );
    }
    return mList;
  }

  public readonly allPlans$ = this.planClaimsStore.plans$;
  public readonly activePlanNumber$ = this.planClaimsStore.activePlanNumber$;
  public readonly activePlan$ = this.planClaimsStore.activePlan$;
  public readonly planClaims$ = this.planClaimsStore.planClaims$;
  public readonly assistance$ = this.planClaimsStore.assistance$;
  public readonly loadingStatus$ = this.planClaimsStore.status$;
}
